import { Box, Paper } from '@mui/material';
import { ReactNode } from 'react';
import { SwitchElement, useFormContext, useWatch } from 'react-hook-form-mui';

type PublishToggleCardProps = {
  icon: ReactNode;
  label: ReactNode;
  switchName: string;
  children?: ReactNode;
  /** When true, the switch and label toggle are disabled. */
  disabled?: boolean;
};

/** Outlined card with icon, label, and a form switch for publish/export toggles. */
export function PublishToggleCard({
  icon,
  label,
  switchName,
  children,
  disabled = false,
}: PublishToggleCardProps) {
  const { setValue } = useFormContext();
  const isOn = useWatch({ name: switchName });

  /** Toggles the publish switch when the icon or label is clicked. */
  function handleLabelClick() {
    if (disabled) return;
    setValue(switchName, !isOn);
  }

  return (
    <Paper
      variant="outlined"
      sx={{ borderRadius: 2, overflow: 'hidden', opacity: disabled ? 0.6 : 1 }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 1.5,
          py: 1,
          gap: 1,
        }}
      >
        <Box
          onClick={handleLabelClick}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            minWidth: 0,
            flexGrow: 1,
            cursor: disabled ? 'default' : 'pointer',
          }}
        >
          {icon}
          <Box
            sx={{
              typography: 'body2',
              fontWeight: 500,
              color: 'text.primary',
              whiteSpace: 'nowrap',
              width: '100%',
            }}
          >
            {label}
          </Box>
        </Box>
        <SwitchElement
          name={switchName}
          label=""
          switchProps={{ size: 'small', disabled }}
          sx={{ margin: 0 }}
        />
      </Box>
      {children}
    </Paper>
  );
}
